import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useBoard } from "@/hooks/useBoard";
import { useWaitingQueue } from "@/hooks/useWaitingQueue";
import { StartHere } from "@/screens/cc";
import { IdeaDock } from "@/screens/idea/IdeaDock";
import { ProjectWorkshop } from "@/screens/projects/Workshop";
import { Card, Label, MonoMeta, StateWord } from "@/design/primitives";
import { SOURCE_WORD } from "@/lib/waitingOnJeff";
import MomentumRail from "@/screens/needsme/MomentumRail";
import { useEventsFeed } from "@/screens/shared/useEventsFeed";
import { cn } from "@/lib/utils";

// ── Needs Me (4→2 council, 2026-10-22) ─────────────────────────────────────
// ONE question: what is blocked on MY word?
//
// The page is two tabs — the queue (default) and the workshop — and the tab
// lives in the URL (?tab=workshop) so a link back to a tab keeps working.
//
// The queue is ONE decision deck. Four old surfaces fed it, and each row
// carries the source it came from so Jeff always knows what he is saying yes
// to:
//   goal    — a goal that needs a word (the Desk's needs-a-word list)
//   project — a project sitting in a waiting state (the Projects waiting deck)
//   monitor — a watch that holds until Jeff decides (the Breakage wall)
//   mail    — a message that needs a reply
//
// The one action per row is the action that already exists on the board it
// came from — no new verbs, no new endpoints, no second meaning for "approve".
//
// Density guard: six rows visible, then a count and an expander. One page, one
// question, one deck.

const VISIBLE_ROWS = 6;
const MAX_EXPANDED = 30;

const SourceChip = ({ source }) => (
  <span
    data-testid={`needsme-source-${source}`}
    className="shrink-0 rounded-chip border border-os-border px-2 py-0.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-3"
  >
    {SOURCE_WORD[source] || source}
  </span>
);

// ── the panel under a row (Jeff 10-23: "can I open this up and see more?") ──
// Everything below is already in the row's own payload — the panel only lays
// out what the board carries, in the same plain words. No new verbs, no new
// fetches, nothing that moves on its own.

const DetailPanel = ({ row }) => {
  const entries = (row.details || []).filter(
    (d) => (d.items && d.items.length) || d.text,
  );
  if (!entries.length) {
    return (
      <p
        data-testid={`needsme-detail-empty-${row.slug}`}
        className="text-[0.8125rem] leading-[1.5] text-os-ink-3"
      >
        Kit hasn't written more down on this one — the line above is all there
        is.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2.5">
      {entries.map((d) => (
        <div key={d.key} className="flex flex-col gap-1 sm:flex-row sm:gap-3">
          <Label as="span" className="shrink-0 font-mono sm:w-[9.5rem]">
            {d.label}
          </Label>
          <div className="min-w-0 flex-1">
            {d.text ? (
              <p
                data-testid={`needsme-detail-text-${row.slug}-${d.key}`}
                className="whitespace-pre-line text-[0.8125rem] leading-[1.5] text-os-ink-2"
              >
                {d.text}
              </p>
            ) : null}
            {d.items ? (
              <ol className="flex flex-col gap-1">
                {d.items.map((it) => (
                  <li
                    key={it.key}
                    data-testid={`needsme-detail-item-${row.slug}-${d.key}-${it.key}`}
                    className="rounded-md border border-os-border px-2.5 py-1.5"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-[0.8125rem] leading-[1.45] text-os-ink-2">
                        {it.title}
                      </p>
                      {it.state ? <MonoMeta as="span">{it.state}</MonoMeta> : null}
                    </div>
                    {it.note ? (
                      <p className="mt-0.5 whitespace-pre-line text-[0.75rem] leading-[1.45] text-os-ink-3">
                        {it.note}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ol>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
};

const QueueRow = ({ row, onFlash, index = 0 }) => {
  const busy = row.board?.pending === row.slug;
  const [open, setOpen] = useState(false);
  // The row carries the words its own action speaks back. The page and the
  // WITH YOU rail share the row, so the same words ride both.
  const act = async () => {
    const res = await row.action();
    if (res && row.actionMessage) onFlash?.(row.actionMessage);
  };
  const panelId = `needsme-detail-panel-${row.key.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  return (
    <div
      data-testid={`needsme-row-${row.slug}`}
      data-source={row.source}
      // Jeff 10/26 (gamify): the first card is THE one — "start here" made
      // visual. #1 carries the ink border; the rest stay hairline.
      data-first={index === 0 || undefined}
      className={cn(
        "rounded-card px-3 py-3",
        index === 0
          ? "border border-os-border-strong bg-white"
          : "border border-transparent",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <SourceChip source={row.source} />
        <button
          type="button"
          data-testid={`needsme-toggle-${row.slug}`}
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen((v) => !v)}
          className="min-w-[14rem] flex-1 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-os-ink"
        >
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[0.9375rem] font-medium text-os-ink">
              {row.title}
            </span>
            <span aria-hidden="true" className="font-mono text-[0.75rem] text-os-ink-3">
              {open ? "▾" : "▸"}
            </span>
            <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-ink-3">
              {open ? "Hide" : "Details"}
            </span>
          </span>
          <span className="block text-[0.8125rem] text-os-ink-2">{row.context}</span>
        </button>
        {row.stateWord && (
          <StateWord tone={row.tone || "amber"}>{row.stateWord}</StateWord>
        )}
        {/*
          A row that asks for a decision carries a BUTTON; a row that only
          needs reading (the record's own next step is the close-out, so
          there is nothing to approve) carries a LINK that says what it is.
          A button that fires nothing — or fires the wrong thing — is how
          Jeff got "I can't tell what to approve".
        */}
        {row.action ? (
          <button
            type="button"
            data-testid={`needsme-act-${row.slug}`}
            disabled={busy}
            onClick={act}
            className="rounded-md border border-os-border-strong bg-os-ink px-3 py-1.5 font-mono text-[0.71875rem] text-white hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Moving it…" : row.actionLabel}
          </button>
        ) : null}
        {!row.action && row.linkTo ? (
          <Link
            to={row.linkTo}
            data-testid={`needsme-link-${row.slug}`}
            className="rounded-md border border-os-border px-3 py-1.5 font-mono text-[0.71875rem] text-os-ink hover:border-os-border-strong"
          >
            {row.actionLabel || row.linkLabel || "Open it"}
          </Link>
        ) : null}
        {row.action && row.linkTo ? (
          <Link
            to={row.linkTo}
            data-testid={`needsme-link-${row.slug}`}
            className="rounded-md border border-os-border px-3 py-1.5 font-mono text-[0.71875rem] text-os-ink hover:border-os-border-strong"
          >
            {row.linkLabel || "Open it"}
          </Link>
        ) : null}
      </div>
      {open ? (
        <div
          id={panelId}
          role="region"
          aria-label={`${row.title} — more detail`}
          data-testid={`needsme-detail-${row.slug}`}
          className="mt-3 border-t border-os-border pt-3"
        >
          <DetailPanel row={row} />
        </div>
      ) : null}
    </div>
  );
};

export default function NeedsMePage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const work = useBoard("work");
  const workItems = work.items;
  // The one truth: five boards, one waiting-on-Jeff queue. The Needs Me
  // banner and the WITH YOU rail on In Flight both read THIS, so their
  // counts can never disagree. (Jeff 2026-10-23: "166 things wait" vs
  // "Nothing waiting on you" on the same morning.)
  const { rows, count, boards, liveness, note } = useWaitingQueue();
  const goals = boards.goals;
  const projects = boards.projects;
  const goalItems = goals.items;
  const [params, setParams] = useSearchParams();
  const [flash, setFlash] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // ── The momentum rail (Jeff 10/26: gamify) — derived from the same boards
  // the queue reads, never a second source of truth.
  const events = useEventsFeed();
  const bySource = useMemo(() => {
    const m = {};
    for (const r of rows) m[r.source] = (m[r.source] || 0) + 1;
    return m;
  }, [rows]);
  const breakage = Array.isArray(boards.breakage?.items) ? boards.breakage.items : [];
  const healthyMonitors = breakage.filter((t) => {
    const s = String(t?.payload?.state || t?.state || "").toUpperCase();
    return s === "OK" || s === "PASS" || s === "HEALTHY";
  }).length;

  const tab = params.get("tab") === "workshop" ? "workshop" : "queue";
  const setTab = (next) => {
    const p = new URLSearchParams(params);
    if (next === "queue") p.delete("tab");
    else p.set("tab", next);
    setParams(p);
  };

  const visible = expanded ? rows.slice(0, MAX_EXPANDED) : rows.slice(0, VISIBLE_ROWS);
  const hidden = rows.length - visible.length;

  const firstWait = rows[0];
  // The count both surfaces show. "166 things wait" / "166 wait" — one
  // number, one truth, two screens.
  const countText =
    count === null
      ? "Some things wait"
      : `${count} ${count === 1 ? "thing waits" : "things wait"}`;
  // The banner count IS the queue count — the same number the WITH YOU rail
  // on In Flight shows. A board that failed means the number is not the whole
  // truth, so the banner says so rather than reading as a smaller, wrong
  // number.
  // The banner count IS the queue count — the same number the WITH YOU rail
  // on In Flight shows. A board that failed means the number is not the whole
  // truth, so the banner says so rather than reading as a smaller, wrong
  // number.
  const startHere = !liveness.anyLive
    ? {
        tone: "muted",
        text: "The boards are reconnecting — the queue fills the moment they're back",
      }
    : firstWait
      ? {
          tone: "amber",
          text: `${countText} on your word — "${firstWait.title}" is the next one`,
          // The banner's button is the FIRST row's action. A row that only
          // needs reading (no approval to give) carries no action, so the
          // banner offers no button — it never fires something that is not
          // there.
          ...(firstWait.action
            ? {
                button: {
                  label: firstWait.actionLabel || "Approve",
                  disabled: Boolean(goals.pending) || Boolean(projects.pending),
                  onClick: async () => {
                    const res = await firstWait.action();
                    if (res && firstWait.actionMessage)
                      setFlash(firstWait.actionMessage);
                  },
                },
              }
            : {}),
        }
      : {
          tone: "green",
          text: "All clear — nothing needs your word. Kit is on everything else.",
        };

  // The ideas waiting to be shaped — the right column of the workshop tab.
  const shapedIdeas = useMemo(() => {
    const items = goalItems || [];
    return items
      .filter((r) => r.kind === "idea" && r.state !== "ARCHIVED")
      .map((r) => r.payload || {});
  }, [goalItems]);

  // The needs-a-goal dock: work that never got a goal. It rides the capture
  // dock (not a second list) so the ideas and the goal-less work sit in one
  // place — deduped by slug.
  const needsGoalRows = useMemo(() => {
    const items = workItems || [];
    return items
      .filter((r) => r.kind === "task")
      .filter((r) => r.payload?.goalRef === "needs-a-goal")
      .filter((r) => r.state !== "ARCHIVED")
      .map((r) => ({
        slug: r.slug,
        kind: r.kind,
        state: r.state,
        payload: {
          title: r.payload?.title,
          why: r.payload?.why,
          next: r.payload?.next,
        },
      }));
  }, [workItems]);

  const workshopFrame = cn(
    desktop ? "grid grid-cols-2 gap-5" : "flex flex-col gap-5",
  );

  return (
    <div data-testid="needsme-page" className="w-full font-inter text-os-ink">
      <div
        className={cn(
          desktop
            ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6"
            : "flex flex-col gap-3 px-4 pb-8 pt-4",
        )}
      >
        <StartHere {...startHere} testId="needsme-start-here" />
        {flash && (
          <p
            data-testid="needsme-flash"
            className="text-[0.875rem] font-medium text-os-green"
          >
            {flash}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2" data-testid="needsme-tabs">
          <button
            type="button"
            data-testid="needsme-tab-queue"
            aria-pressed={tab === "queue"}
            onClick={() => setTab("queue")}
            className={cn(
              "rounded-control border px-3 py-1.5 font-mono text-[0.75rem]",
              tab === "queue"
                ? "border-os-border-strong bg-os-ink text-white"
                : "border-os-border text-os-ink-2 hover:border-os-border-strong",
            )}
          >
            Queue
          </button>
          <button
            type="button"
            data-testid="needsme-tab-workshop"
            aria-pressed={tab === "workshop"}
            onClick={() => setTab("workshop")}
            className={cn(
              "rounded-control border px-3 py-1.5 font-mono text-[0.75rem]",
              tab === "workshop"
                ? "border-os-border-strong bg-os-ink text-white"
                : "border-os-border text-os-ink-2 hover:border-os-border-strong",
            )}
          >
            Workshop
          </button>
        </div>

        {tab === "queue" ? (
          <div data-testid="needsme-queue" className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card
              as="section"
              className="px-5 py-4 lg:col-span-2 lg:col-start-1 lg:row-start-1"
            >
              <Label as="h2" data-testid="needsme-count">
                Waiting on your word — {count ?? "—"}
              </Label>
              <p className="mt-1 text-[0.8125rem] text-os-ink-3">
                One answer each: approve to move the work, set aside to let it
                rest, or leave a note so Kit knows where you stand.
              </p>
              {note ? (
                <p
                  data-testid="needsme-queue-note"
                  className="mt-1 text-[0.8125rem] text-os-amber"
                >
                  {note}
                </p>
              ) : null}
              {rows.length === 0 ? (
                <Card quiet className="mt-3 px-4 py-3">
                  <p className="text-[0.875rem] text-os-ink-2">
                    Nothing is waiting on you right now — the deck is empty.
                  </p>
                </Card>
              ) : (
                <div
                  data-testid="needsme-queue-grid"
                  className="mt-2 grid grid-cols-1 items-start gap-x-5 lg:grid-cols-2"
                >
                  {visible.map((row, i) => (
                    <QueueRow key={row.key} row={row} index={i} onFlash={setFlash} />
                  ))}
                </div>
              )}
              {hidden > 0 && (
                <div className="border-t border-os-border pt-3">
                  <button
                    type="button"
                    data-testid="needsme-more"
                    onClick={() => setExpanded((v) => !v)}
                    className="font-mono text-[0.75rem] text-os-ink-3 underline-offset-4 hover:text-os-ink hover:underline"
                  >
                    {expanded
                      ? "Show fewer"
                      : `+${hidden} more waiting — show them`}
                  </button>
                </div>
              )}
            </Card>

            <MomentumRail
              count={count}
              events={events}
              bySource={bySource}
              healthyMonitors={healthyMonitors}
              live={liveness.anyLive}
            />

            <IdeaDock
              onFlash={setFlash}
              desktop={desktop}
              extraItems={needsGoalRows}
              extraLabel={
                needsGoalRows.length
                  ? `${needsGoalRows.length} pieces of work have no goal yet — they ride here until one is written down`
                  : ""
              }
            />
          </div>
        ) : (
          <div data-testid="needsme-workshop" className="flex flex-col gap-4">
            <div className={workshopFrame}>
              <div className="flex flex-col gap-3">
                <ProjectWorkshop flash={setFlash} desktop={desktop} />
              </div>
              <div className="flex flex-col gap-3">
                <Card as="section" className="px-5 py-4">
                  <Label as="h2">Ideas waiting to be shaped</Label>
                  {shapedIdeas.length === 0 ? (
                    <p className="mt-2 text-[0.875rem] text-os-ink-2">
                      Nothing raw is waiting — type an idea on the Queue tab and
                      it lands here to shape.
                    </p>
                  ) : (
                    <ul className="mt-2 flex flex-col gap-2">
                      {shapedIdeas.slice(0, VISIBLE_ROWS).map((p, i) => (
                        <li
                          key={i}
                          data-testid="needsme-workshop-idea"
                          className="border-t border-os-border pt-2 first:border-t-0 first:pt-0"
                        >
                          <p className="text-[0.9375rem] font-medium text-os-ink">
                            {p.title || "Untitled idea"}
                          </p>
                          {p.why && (
                            <p className="text-[0.8125rem] text-os-ink-2">{p.why}</p>
                          )}
                        </li>
                      ))}
                      {shapedIdeas.length > VISIBLE_ROWS && (
                        <MonoMeta as="p">
                          +{shapedIdeas.length - VISIBLE_ROWS} more waiting to be
                          shaped
                        </MonoMeta>
                      )}
                    </ul>
                  )}
                </Card>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
